import { useCallback } from 'react';

import { useIntl } from 'react-intl';

import { getProductPricingConfig } from '../calculator/product-pricing-configs';
import { resolveBillingCycle } from '../calculator/resolve-billing-cycle';

import { STEP_ORDER } from './constants';
import { buildDefaultProduct, useUbpContext } from './context';
import { Selector, selectCategoryHasSelections, selectOrgUserCountError } from './selectors';
import {
	ORG_USER_COUNT_MAX,
	ORG_USER_COUNT_MIN,
	orgUserCountErrorMessages,
	validateOrgUserCount,
} from './validation';

import type { Meter, Product, ProductCategory, ProductInputs, Step, UsageType } from './types';
import type { Edition, ProductKey } from '../calculator/types';

export const useSelector = <T>(selector: Selector<T>): T => selector(useUbpContext());

// --- flow navigation + setters ---

export const useSetStep = () => {
	const { setStep } = useUbpContext();
	return useCallback((step: Step) => setStep(step), [setStep]);
};

export const useGoToNextStep = () => {
	const { step, setStep } = useUbpContext();
	return useCallback(() => {
		const next = STEP_ORDER[STEP_ORDER.indexOf(step) + 1];
		if (next) setStep(next);
	}, [step, setStep]);
};

export const useGoToPrevStep = () => {
	const { step, setStep } = useUbpContext();
	return useCallback(() => {
		const prev = STEP_ORDER[STEP_ORDER.indexOf(step) - 1];
		if (prev) setStep(prev);
	}, [step, setStep]);
};

export const useSetMeter = () => {
	const { setMeter } = useUbpContext();
	return useCallback((meter: Meter) => setMeter(meter), [setMeter]);
};

export const useSetUsageType = () => {
	const { setUsageType } = useUbpContext();
	return useCallback((usageType: UsageType) => setUsageType(usageType), [setUsageType]);
};

export const useSetUsage = () => {
	const { setUsage } = useUbpContext();
	return useCallback((usage: number) => setUsage(usage), [setUsage]);
};

export const useSetOrgUserCount = () => {
	const { setOrgUserCount, setOrgUserCountError } = useUbpContext();
	return useCallback(
		(orgUserCount: number | undefined) => {
			setOrgUserCountError(validateOrgUserCount(orgUserCount));
			setOrgUserCount(orgUserCount);
		},
		[setOrgUserCount, setOrgUserCountError],
	);
};

export const useOrgUserCountErrorMessage = (): string | undefined => {
	const intl = useIntl();
	const error = useSelector(selectOrgUserCountError);
	if (!error) return undefined;
	const message = orgUserCountErrorMessages[error];
	return intl.formatMessage(message, {
		min: ORG_USER_COUNT_MIN,
		max: ORG_USER_COUNT_MAX,
	});
};

// --- per-product input mutations ---

export const useSetProductEdition = () => {
	const update = useUpdateProductInputs();
	return useCallback(
		(productKey: ProductKey, edition: Edition) =>
			update(productKey, {
				edition,
				billingCycle: resolveBillingCycle(productKey, edition),
			}),
		[update],
	);
};

export const useSetProductUnitCount = () => {
	const update = useUpdateProductInputs();
	return useCallback(
		(productKey: ProductKey, unitCount: number) => update(productKey, { unitCount }),
		[update],
	);
};

// --- selection (add / remove / category-aware toggle) ---

export const useAddProduct = () => {
	const { setProducts } = useUbpContext();
	return useCallback(
		(productKey: ProductKey) => {
			setProducts((prev) =>
				prev.some((product) => product.productKey === productKey)
					? prev
					: [...prev, buildDefaultProduct(productKey)],
			);
		},
		[setProducts],
	);
};

export const useClearProducts = () => {
	const { setProducts } = useUbpContext();
	return useCallback(() => setProducts([]), [setProducts]);
};

/**
 * Allowance-step tab switch. Changing category clears the current selection so
 * the user starts fresh on the new tab; selecting the active tab is a no-op.
 */
export const useChangeAllowanceTab = (
	tab: ProductCategory,
	setTab: (tab: ProductCategory) => void,
) => {
	const { setProductTabSelectionWarningShown } = useUbpContext();
	const hasSelections = useSelector(selectCategoryHasSelections(tab));
	const clearProducts = useClearProducts();
	return (next: ProductCategory) => {
		if (next === tab) return;
		setProductTabSelectionWarningShown(hasSelections);
		clearProducts();
		setTab(next);
	};
};

export const useRemoveProduct = () => {
	const { setProducts } = useUbpContext();
	return useCallback(
		(productKey: ProductKey) => {
			setProducts((prev) => prev.filter((product) => product.productKey !== productKey));
		},
		[setProducts],
	);
};

/**
 * Step 4 selection toggle. Selecting a product seeds its inputs from config
 * defaults but overrides `unitCount` with the org size. Apps and collections
 * are mutually exclusive, so selecting one category clears any products of the
 * other.
 */
export const useToggleProduct = () => {
	const { setProducts, orgUserCount } = useUbpContext();
	return useCallback(
		(key: ProductKey, selected: boolean) => {
			const category = getProductPricingConfig(key).category;
			setProducts((prev) => {
				if (!selected) {
					return prev.filter((p) => p.productKey !== key);
				}
				const sameCategory = prev.filter(
					(p) =>
						p.productKey !== key && getProductPricingConfig(p.productKey).category === category,
				);
				const base = buildDefaultProduct(key);
				const seeded: Product = {
					...base,
					inputs: {
						...base.inputs,
						unitCount: orgUserCount ?? base.inputs.unitCount,
					},
				};
				return [...sameCategory, seeded];
			});
		},
		[setProducts, orgUserCount],
	);
};

const useUpdateProductInputs = () => {
	const { setProducts } = useUbpContext();
	return useCallback(
		(productKey: ProductKey, patch: Partial<ProductInputs>) => {
			setProducts((prev) =>
				prev.map((product) =>
					product.productKey === productKey
						? {
								...product,
								inputs: { ...product.inputs, ...patch },
							}
						: product,
				),
			);
		},
		[setProducts],
	);
};
